import { Alert, Button, Card, CardBody, Input, Spinner, Textarea } from '@foundation/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import styles from './collection-pages.$id.module.css'
import { api, type CollectionPage, API_BASE_URL } from '@/lib/api'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/collection-pages/$id')({
  component: CollectionPageDetail,
})

type Tab = 'overview' | 'share' | 'settings'

function CollectionPageDetail() {
  const { id } = Route.useParams()
  const navigate = useNavigate()

  const [page, setPage] = useState<CollectionPage | null>(null)
  const [stats, setStats] = useState<Record<string, unknown> | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('overview')

  // Settings form state
  const [formData, setFormData] = useState({
    name: '',
    headline: '',
    description: '',
    brandColor: '#6366f1',
    thankYouMessage: '',
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const [pageResponse, statsResponse] = await Promise.all([
          api.getCollectionPage(id),
          api.getCollectionPageStats(id).catch(() => ({ data: {} })),
        ])
        setPage(pageResponse.data)
        setStats(statsResponse.data)
        setFormData({
          name: pageResponse.data.name,
          headline: pageResponse.data.headline,
          description: pageResponse.data.description ?? '',
          brandColor: pageResponse.data.brandColor,
          thankYouMessage: pageResponse.data.thankYouMessage,
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load collection page')
      } finally {
        setIsLoading(false)
      }
    }
    void fetchData()
  }, [id])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim() || !formData.headline.trim()) {
      toast.error('Name and headline are required')
      return
    }

    try {
      setIsSaving(true)
      const request: Parameters<typeof api.updateCollectionPage>[1] = {
        name: formData.name.trim(),
        headline: formData.headline.trim(),
        brandColor: formData.brandColor,
      }
      if (formData.description.trim()) request.description = formData.description.trim()
      if (formData.thankYouMessage.trim()) request.thankYouMessage = formData.thankYouMessage.trim()
      const response = await api.updateCollectionPage(id, request)
      setPage(response.data)
      toast.success('Collection page updated')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update collection page')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this collection page? This action cannot be undone.'
      )
    ) {
      return
    }
    try {
      setIsDeleting(true)
      await api.deleteCollectionPage(id)
      toast.success('Collection page deleted')
      void navigate({ to: '/collection-pages' })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete collection page')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleCopyUrl = () => {
    if (!page) return
    const publicUrl = `${API_BASE_URL}/collect/${page.slug}`
    void navigator.clipboard.writeText(publicUrl)
    toast.success('URL copied to clipboard')
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'overview', label: 'Overview', icon: '\uD83D\uDCCA' },
    { id: 'share', label: 'Share', icon: '\uD83D\uDD17' },
    { id: 'settings', label: 'Settings', icon: '\u2699\uFE0F' },
  ]

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="lg" />
        <p>Loading collection page...</p>
      </div>
    )
  }

  if (error || !page) {
    return <Alert variant="danger">{error ?? 'Collection page not found'}</Alert>
  }

  return (
    <>
      {/* Tabs */}
      <div className={styles.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span className={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Stats */}
          <div className={styles.statsGrid}>
            <Card className={styles.statCard}>
              <CardBody>
                <div className={styles.statValue}>{(stats?.visits as number) ?? 0}</div>
                <div className={styles.statLabel}>Page Visits</div>
              </CardBody>
            </Card>
            <Card className={styles.statCard}>
              <CardBody>
                <div className={styles.statValue}>{(stats?.submissions as number) ?? 0}</div>
                <div className={styles.statLabel}>Submissions</div>
              </CardBody>
            </Card>
            <Card className={styles.statCard}>
              <CardBody>
                <div className={styles.statValue}>{(stats?.conversionRate as number) ?? 0}%</div>
                <div className={styles.statLabel}>Conversion Rate</div>
              </CardBody>
            </Card>
          </div>

          {/* Page Info */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Page Details</h3>
            <Card>
              <CardBody>
                <div className={styles.infoGrid}>
                  <span className={styles.infoLabel}>Name</span>
                  <span className={styles.infoValue}>{page.name}</span>

                  <span className={styles.infoLabel}>Slug</span>
                  <span className={styles.infoValue}>/{page.slug}</span>

                  <span className={styles.infoLabel}>Headline</span>
                  <span className={styles.infoValue}>{page.headline}</span>

                  {page.description && (
                    <>
                      <span className={styles.infoLabel}>Description</span>
                      <span className={styles.infoValue}>{page.description}</span>
                    </>
                  )}

                  <span className={styles.infoLabel}>Brand Color</span>
                  <span className={styles.infoValue}>
                    <span
                      className={styles.colorSwatch}
                      style={{ backgroundColor: page.brandColor }}
                    />
                    {page.brandColor}
                  </span>

                  <span className={styles.infoLabel}>Created</span>
                  <span className={styles.infoValue}>{formatDate(page.createdAt)}</span>

                  <span className={styles.infoLabel}>Last Updated</span>
                  <span className={styles.infoValue}>{formatDate(page.updatedAt)}</span>
                </div>
              </CardBody>
            </Card>
          </div>
        </>
      )}

      {/* Share Tab */}
      {activeTab === 'share' && (
        <>
          <div className={styles.shareSection}>
            <h3 className={styles.shareSectionTitle}>Public Collection URL</h3>
            <div className={styles.urlBox}>
              <span className={styles.urlText}>
                {API_BASE_URL}/collect/{page.slug}
              </span>
              <Button size="sm" variant="primary" onClick={handleCopyUrl}>
                Copy URL
              </Button>
            </div>
          </div>

          <div className={styles.shareSection}>
            <h3 className={styles.shareSectionTitle}>QR Code</h3>
            <div className={styles.qrPlaceholder}>QR Code generation coming soon</div>
          </div>
        </>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <>
          <Card>
            <CardBody>
              <form className={styles.form} onSubmit={(e) => void handleSave(e)}>
                <Input
                  label="Page Name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Customer Feedback"
                  required
                />

                <Input
                  label="Headline"
                  type="text"
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  placeholder="e.g., We'd love your feedback!"
                  required
                />

                <Textarea
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Tell your customers what kind of feedback you're looking for..."
                  rows={3}
                />
                <p className={styles.fieldHint}>Optional. Shown below the headline.</p>

                <div className={styles.colorField}>
                  <span className={styles.colorFieldLabel}>Brand Color</span>
                  <div className={styles.colorInput}>
                    <input
                      type="color"
                      className={styles.colorPicker}
                      value={formData.brandColor}
                      onChange={(e) => setFormData({ ...formData, brandColor: e.target.value })}
                    />
                    <span className={styles.colorValue}>{formData.brandColor}</span>
                  </div>
                </div>

                <Input
                  label="Thank You Message"
                  type="text"
                  value={formData.thankYouMessage}
                  onChange={(e) => setFormData({ ...formData, thankYouMessage: e.target.value })}
                  placeholder="Message shown after submission"
                />
                <p className={styles.fieldHint}>
                  Shown to customers after they submit a testimonial.
                </p>

                <div className={styles.formActions}>
                  <Button type="submit" variant="primary" loading={isSaving}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>

          {/* Danger Zone */}
          <div className={styles.dangerSection}>
            <Card className={styles.dangerCard}>
              <CardBody>
                <h3 className={styles.dangerTitle}>Danger Zone</h3>
                <p className={styles.dangerDescription}>
                  Deleting this collection page will permanently remove it and all associated
                  testimonials. This action cannot be undone.
                </p>
                <Button variant="danger" onClick={() => void handleDelete()} loading={isDeleting}>
                  Delete Collection Page
                </Button>
              </CardBody>
            </Card>
          </div>
        </>
      )}
    </>
  )
}
